import axios from "axios";
import { API_BASE_URL } from "../config";

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Interceptor para agregar token a las peticiones
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para manejar errores de respuesta
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Solo eliminar token si es un error de autenticación específico
    if (
      error.response?.status === 401 &&
      error.response?.data?.message !== "Token no proporcionado"
    ) {
      // Verificar si estamos en una ruta que no sea login para evitar loops
      const currentPath = window.location.pathname;
      if (!currentPath.includes("/login")) {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  }
);

// Auth services
export const authService = {
  login: (credentials) => api.post("/auth/login", credentials),
  getCurrentUser: () => api.get("/auth/me"),
  verifyToken: async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return false;

      const response = await api.get("/auth/me");
      return response.status === 200;
    } catch (error) {
      return false;
    }
  },
};

// Users services
export const userService = {
  getAll: () => api.get("/usuarios"),
  getById: (id) => api.get(`/usuarios/${id}`),
  create: (userData) => api.post("/usuarios", userData),
  update: (id, userData) => api.put(`/usuarios/${id}`, userData),
  changePassword: (id, password) =>
    api.put(`/usuarios/${id}/password`, { password }),
  toggleStatus: (id) => api.put(`/usuarios/${id}/toggle-status`),
};

// Gastos services
export const gastosService = {
  getAll: (params) => api.get("/gastos", { params }),
  getById: (id) => api.get(`/gastos/${id}`),
  create: (gastoData) => api.post("/gastos", gastoData),
  updateStatus: (id, statusData) => api.put(`/gastos/${id}/estado`, statusData),
  getTipos: () => api.get("/gastos/tipos/list"),
  getEmpleados: () => api.get("/employees/list"),
  delete: (id) => api.delete(`/gastos/${id}`),
  updateGasto: (id, data) => api.put(`/gastos/${id}/editar`, data),
  exportarExcel: (fechaDesde, fechaHasta) =>
    api.get(
      `/gastos/exportar-excel?fechaDesde=${fechaDesde}&fechaHasta=${fechaHasta}`,
      {
        responseType: "blob",
      }
    ),

};

// Empleados services
export const tiposService = {

  // Servicios para tipos de empleados
  createTipo: (tipoData) => {
    console.log("Llamando a createTipo con:", tipoData);
    console.log("URL construida:", "/tipos/tiposCreate");
    return api.post("/tipos/tiposCreate", tipoData);
  },
  updateTipo: (id, tipoData) => {
    console.log("Llamando a updateTipo con:", { id, tipoData });
    console.log("URL construida:", `/tipos/tiposUpdate/${id}`);
    return api.put(`/tipos/tiposUpdate/${id}`, tipoData);
  },
  deleteTipo: (id) => api.delete(`/tipos/tiposDelete/${id}`),


  getDepartamentos: (dptoFuncion) => api.get("/tipos/departamentos", { params: { dptoFuncion } }),
  getDepartamentoById: (id) => api.get(`/tipos/departamentos/${id}`),
  getContratoHoras: (contratoHoras) => api.get("/tipos/contrato-horas", { params: { contratoHoras } }),
  getRelacionLaboral: (relacionLaboral) => api.get("/tipos/relacion-laboral", { params: { relacionLaboral } }),
  getTipos: () => api.get("/tipos/tipos"),
  getSeguimientoFactorialTipos: (factorialId = null) => api.get("/tipos/seguimiento-factorial-tipos-list", { params: { factorialId } }),
  getDocumentosTipos: () => api.get("/tipos/documentos-tipos"),
  // Documentos obligatorios por departamento
  getDocumentosObligatoriosDepartamento: (departamentoId) => api.get(`/tipos/documentos-obligatorios-departamento/${departamentoId}`),

};

// Empleados services
export const empleadosService = {
  buscar: (params) => api.get("/employees/buscar", { params }),
  getDetail: (empleadoId) => api.get(`/employees/detail/${empleadoId}`),
  enviarRecordatorio: (crewId) =>
    api.post("/auth/enviar-mensaje-validar-gastos", { crewId }),
  importEmployees: () => api.post("/employees/import"),
  update: (empleadoId, empleadoData) => api.put(`/employees/update/${empleadoId}`, empleadoData),
  getDepartamentos: (dptoFuncion) => api.get("/employees/departamentos", { params: { dptoFuncion } }),
  getContratoHoras: (contratoHoras) => api.get("/employees/contrato-horas", { params: { contratoHoras } }),
  getRelacionLaboral: (relacionLaboral) => api.get("/employees/relacion-laboral", { params: { relacionLaboral } }),
  uploadDocument: (empleadoId, documentType, file, validityDate) => {
    console.log('=== SERVICIO API UPLOAD DOCUMENT ===');
    console.log('empleadoId:', empleadoId);
    console.log('documentType:', documentType);
    console.log('file:', file);
    console.log('validityDate:', validityDate);

    const formData = new FormData();
    formData.append('document', file);
    formData.append('empleadoId', empleadoId);
    formData.append('documentType', documentType);

    // Solo agregar validityDate si no es null, undefined o cadena vacía
    if (validityDate !== null && validityDate !== undefined && validityDate !== '') {
      formData.append('validityDate', validityDate);
    }

    console.log('FormData creado, enviando petición...');
    console.log('FormData entries:');
    for (let [key, value] of formData.entries()) {
      console.log(`${key}: ${value}`);
    }

    return api.post("/employees/upload-document", formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  updateDocumentValidity: (empleadoId, documentType, validityDate) => {
    return api.put("/employees/update-document-validity", {
      empleadoId,
      documentType,
      validityDate
    });
  },
  // Obtener tipos de documentos desde el procedimiento almacenado
  getDocumentTypes: () => api.get("/employees/document-types"),
  // Obtener documentos asociados a un empleado específico
  getEmployeeDocuments: (empleadoId) => api.get(`/employees/employee-documents/${empleadoId}`),
  // Actualizar el estado obligatorio de un documento
  updateDocumentObligatory: (empleadoId, documentoId, obligatorio) => api.put("/employees/update-document-obligatory", {
    empleadoId,
    documentoId,
    obligatorio
  }),
  // Eliminar fichero de documento del empleado
  deleteDocumentFile: (empleadoId, documentoId) => api.delete(`/employees/delete-document-file`, {
    data: {
      empleadoId,
      documentoId
    }
  }),
  downloadDocumentFile: (empleadoId, documentoTipoId) =>
    api.get(`/employees/download-document-file/${empleadoId}/${documentoTipoId}`, {
      responseType: "blob",
    }),
  // Descargar documentos seleccionados
  downloadSelectedDocuments: (empleadoId, documentosSeleccionados) => api.post(`/employees/download-selected-documents`, {
    empleadoId,
    documentosSeleccionados
  }, {
    responseType: 'blob'
  }),
  // Descargar todos los documentos del empleado
  downloadAllDocuments: (empleadoId) => api.post(`/employees/download-all-documents`, {
    empleadoId
  }, {
    responseType: 'blob'
  }),
  // Verificar y actualizar la validación del empleado
  checkEmployeeValidation: (empleadoId) => api.post(`/employees/check-employee-validation/${empleadoId}`),
  // Obtener tipos de seguimiento factorial
  getSeguimientoFactorialTipos: (empleadoId) => api.get(`/employees/seguimiento-factorial-tipos/${empleadoId}`),
  // Guardar seguimiento factorial
  saveSeguimientoFactorial: (empleadoId, factoresIds) => api.post(`/employees/save-seguimiento-factorial`, {
    empleadoId,
    factoresIds
  }),
  // Enviar documento por email
  sendDocumentByEmail: (formData) => api.post("/employees/send-document-email", formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
  // Obtener estadísticas de documentos de trabajadores
  getDocumentStats: () => api.get("/employees/document-stats"),
  getDocumentosCentralizados: (params) =>
    api.get("/employees/documents-centralized", { params }),
};

// Administradores services
export const administradoresService = {
  buscar: (params) => api.get("/administradores/buscar", { params }),
  create: (data) => api.post("/administradores", data),
  update: (id, data) => api.put(`/administradores/${id}`, data),
  delete: (id) => api.delete(`/administradores/${id}`),
};

// Transportistas services
export const transportistasService = {
  buscar: (params) => api.get('/transportistas/buscar', { params }),
  getDetail: (id) => api.get(`/transportistas/detail/${id}`),
  create: (data) => api.post('/transportistas', data),
  update: (id, data) => api.put(`/transportistas/${id}`, data),
  getEmpleadosPrl: (search) => api.get('/transportistas/empleados-prl', { params: { search } }),
  delete: (id) => api.delete(`/transportistas/${id}`),
};

// Vehículos services
export const vehiculosService = {
  buscar: (params) => api.get('/vehiculos/buscar', { params }),
  getDetail: (id) => api.get(`/vehiculos/detail/${id}`),
  create: (data) => api.post('/vehiculos', data),
  update: (id, data) => api.put(`/vehiculos/${id}`, data),
  listByTransportista: (transportistaId) => api.get(`/vehiculos/by-transportista/${transportistaId}`),
  delete: (id) => api.delete(`/vehiculos/${id}`),
  getDocumentTypes: () => api.get('/vehiculos/document-types'),
  listDocuments: (vehiculoId) => api.get(`/vehiculos/documents/by-vehiculo/${vehiculoId}`),
  uploadDocument: (vehiculoId, tipoDocumentoId, fechaFin, file) => {
    const formData = new FormData();
    formData.append('document', file);
    formData.append('tipoDocumentoId', tipoDocumentoId);
    formData.append('fechaFin', fechaFin);
    return api.post(`/vehiculos/documents/upload/${vehiculoId}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  updateDocument: (docId, tipoDocumentoId, fechaFin) => api.put(`/vehiculos/documents/${docId}`, { tipoDocumentoId, fechaFin }),
  deleteDocument: (docId) => api.delete(`/vehiculos/documents/${docId}`),
  downloadDocument: (docId) => api.get(`/vehiculos/documents/download/${docId}`, { responseType: 'blob' }),
  replaceDocumentFile: (docId, file) => {
    const formData = new FormData();
    formData.append('document', file);
    return api.post(`/vehiculos/documents/replace-file/${docId}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  // Incidencias
  listIncidencias: (vehiculoId) => api.get(`/vehiculos/incidencias/by-vehiculo/${vehiculoId}`),
  createIncidencia: (data) => api.post('/vehiculos/incidencias', data),
  updateIncidencia: (id, data) => api.put(`/vehiculos/incidencias/${id}`, data),
  deleteIncidencia: (id) => api.delete(`/vehiculos/incidencias/${id}`),
  listIncidenciaFiles: (incidenciaId) => api.get(`/vehiculos/incidencias/${incidenciaId}/files`),
  uploadIncidenciaFile: (incidenciaId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/vehiculos/incidencias/${incidenciaId}/files`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  deleteIncidenciaFile: (fileId) => api.delete(`/vehiculos/incidencias/files/${fileId}`),
  downloadIncidenciaFile: (fileId) => api.get(`/vehiculos/incidencias/files/download/${fileId}`, { responseType: 'blob' }),
};

// Empresa services (similar a empleados)
export const empresaService = {
  // Obtener tipos de documentos desde el procedimiento almacenado
  getDocumentTypes: () => api.get("/empresa/document-types"),
  // Obtener documentos asociados a la empresa
  getCompanyDocuments: () => api.get("/empresa/company-documents"),
  // Subir documento
  uploadDocument: (documentType, file, validityDate) => {
    console.log('=== SERVICIO API UPLOAD DOCUMENT EMPRESA ===');
    console.log('documentType:', documentType);
    console.log('file:', file);
    console.log('validityDate:', validityDate);

    const formData = new FormData();
    formData.append('document', file);
    formData.append('documentType', documentType);

    // Solo agregar validityDate si no es null, undefined o cadena vacía
    if (validityDate !== null && validityDate !== undefined && validityDate !== '') {
      formData.append('validityDate', validityDate);
    }

    console.log('FormData creado, enviando petición...');
    console.log('FormData entries:');
    for (let [key, value] of formData.entries()) {
      console.log(`${key}: ${value}`);
    }

    return api.post("/empresa/upload-document", formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  // Actualizar fecha de validez del documento
  updateDocumentValidity: (documentType, validityDate) => {
    return api.put("/empresa/update-document-validity", {
      documentType,
      validityDate
    });
  },
  // Actualizar el estado obligatorio de un documento
  updateDocumentObligatory: (documentoId, obligatorio) => api.put("/empresa/update-document-obligatory", {
    documentoId,
    obligatorio
  }),
  // Eliminar fichero de documento de empresa
  deleteDocumentFile: (documentoId) => api.delete(`/empresa/delete-document-file`, {
    data: {
      documentoId
    }
  }),
  // Descargar documentos seleccionados
  downloadSelectedDocuments: (documentosSeleccionados) => api.post(`/empresa/download-selected-documents`, {
    documentosSeleccionados
  }, {
    responseType: 'blob'
  }),
  // Descargar todos los documentos de empresa
  downloadAllDocuments: () => api.post(`/empresa/download-all-documents`, {}, {
    responseType: 'blob'
  }),
  // Enviar documento por email
  sendDocumentByEmail: (formData) => api.post("/empresa/send-document-email", formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
  // Obtener historial de documentos de empresa
  getDocumentHistory: (documentTypeId, fechaDesde = null, fechaHasta = null) => {
    const params = new URLSearchParams();
    if (fechaDesde) params.append('fechaDesde', fechaDesde);
    if (fechaHasta) params.append('fechaHasta', fechaHasta);
    const queryString = params.toString();
    return api.get(`/empresa/document-history/${documentTypeId}${queryString ? '?' + queryString : ''}`);
  },
  // Descargar archivo del historial
  downloadHistoryFile: (filename) => api.get(`/empresa/document-history/download/${filename}`, { responseType: 'blob' }),
  // Obtener estadísticas de documentos de empresa
  getDocumentStats: () => api.get("/empresa/document-stats"),
};

export default api;
